/**
 * <discount-alert> — alerte « code promo activé » après un lien /discount/CODE (snippets/discount-link-alert.liquid).
 *
 * Shopify pose le cookie discount_code et redirige sans rien dans l'URL : on affiche l'alerte si ce cookie existe, que
 * le code figure dans le réglage « Codes promo envoyés en lien » (Shopify pose le cookie même pour un code inexistant
 * ou expiré) et qu'il n'a pas déjà été annoncé dans les dernières 24 h (mémoire du navigateur, facultative).
 * Fermeture au bouton, au bout de 15 s (fin de l'animation de la barre, en pause au survol et au focus) ou dès un ajout
 * au panier (le toast du panier prend la même place).
 */
const DISCOUNT_ALERT_KEY = 'ewc-code-annonce';
const DISCOUNT_ALERT_DELAY = 24 * 60 * 60 * 1000;

class DiscountAlert extends HTMLElement {
  connectedCallback() {
    const code = this.cookieCode();
    if (!code || this.alreadyShown(code)) return;

    let codes = {};
    try {
      codes = JSON.parse(this.querySelector('[data-discount-alert-codes]').textContent || '{}');
    } catch {
      /* liste illisible : pas d'alerte */
    }
    const amount = codes[code.toUpperCase()];
    if (!amount) return;
    this.remember(code);

    this.querySelector('[data-discount-alert-close]').addEventListener('click', () => this.close());
    this.querySelector('[data-discount-alert-timer]').addEventListener('animationend', () => this.close());
    document.addEventListener('cart:added', () => this.close(), { once: true });

    // Zone role="status" visible d'abord, texte ensuite : les lecteurs d'écran l'annoncent
    this.hidden = false;
    requestAnimationFrame(() => {
      this.renderText(this.dataset.text, code, amount);
      this.classList.add('is-visible');
    });
  }

  cookieCode() {
    const match = document.cookie.match(/(?:^|;\s*)discount_code=([^;]+)/);
    if (!match) return '';
    try {
      return decodeURIComponent(match[1]).trim();
    } catch {
      return '';
    }
  }

  alreadyShown(code) {
    try {
      const last = JSON.parse(localStorage.getItem(DISCOUNT_ALERT_KEY) || 'null');
      return !!last && last.code === code && Date.now() - last.at < DISCOUNT_ALERT_DELAY;
    } catch {
      return false;
    }
  }

  remember(code) {
    try {
      localStorage.setItem(DISCOUNT_ALERT_KEY, JSON.stringify({ code, at: Date.now() }));
    } catch {
      /* navigation privée : l'alerte pourra revenir à la page suivante */
    }
  }

  /* Texte traduit « Code %c de −%a activé… » : le code vient de l'URL, il est inséré en texte, jamais en HTML */
  renderText(template, code, amount) {
    const box = this.querySelector('[data-discount-alert-text]');
    const [before, after = ''] = (template || '').split('%c');
    const strong = document.createElement('strong');
    strong.textContent = code;
    box.replaceChildren(document.createTextNode(before), strong, document.createTextNode(after.replace('%a', amount)));
  }

  close() {
    if (this.hidden || !this.classList.contains('is-visible')) return;
    this.classList.remove('is-visible');
    setTimeout(() => (this.hidden = true), 300);
  }
}

if (!customElements.get('discount-alert')) customElements.define('discount-alert', DiscountAlert);
